import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

import MaterialFormModal from './components/MaterialFormModal';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import { Checkbox } from '../../components/ui/Checkbox';
import materialService from '../../services/materialService';
import materialRealtimeService from '../../services/materialRealtimeService';

const MaterialManagementDatabase = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [deletingMaterial, setDeletingMaterial] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMaterials();

    // Subscribe to real-time changes
    const subscription = materialRealtimeService?.subscribeMaterials({
      onInsert: (newMaterial) => {
        // Add new material to the list
        setMaterials(prev => [newMaterial, ...prev]);
      },
      onUpdate: (updatedMaterial) => {
        // Update existing material in the list
        setMaterials(prev => 
          prev?.map(m => m?.id === updatedMaterial?.id ? updatedMaterial : m)
        );
      },
      onDelete: (deletedMaterial) => {
        // Remove deleted material from the list
        setMaterials(prev => 
          prev?.filter(m => m?.id !== deletedMaterial?.id)
        );
        // Remove from selected materials if it was selected
        setSelectedMaterials(prev => 
          prev?.filter(id => id !== deletedMaterial?.id)
        );
      }
    });

    // Cleanup subscription on unmount
    return () => {
      materialRealtimeService?.unsubscribeMaterials();
    };
  }, []);

  const fetchMaterials = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await materialService?.getAllMaterials();
    if (error) {
      setError(error);
      setMaterials([]);
    } else {
      setMaterials(data || []);
      setFilteredMaterials(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    let result = materials;

    if (filterDepartment !== 'all') {
      result = result?.filter(m => m?.department === filterDepartment);
    }

    if (filterType !== 'all') {
      result = result?.filter(m => m?.material_type === filterType);
    }

    if (filterStatus !== 'all') {
      result = result?.filter(m => m?.status === filterStatus);
    }

    if (searchQuery) {
      result = result?.filter(m =>
        m?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        m?.sku?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        m?.material_category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    setFilteredMaterials(result);
  }, [materials, searchQuery, filterDepartment, filterType, filterStatus]);

  const handleSelectMaterial = (materialId) => {
    setSelectedMaterials(prev =>
      prev?.includes(materialId) ?
      prev?.filter(id => id !== materialId) :
      [...prev, materialId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMaterials?.length === filteredMaterials?.length) {
      setSelectedMaterials([]);
    } else {
      setSelectedMaterials(filteredMaterials?.map(m => m?.id));
    }
  };

  const handleCreateMaterial = async (materialData) => {
    const { data, error } = await materialService?.createMaterial(materialData);
    if (error) {
      alert(`Error creating material: ${error}`);
    } else {
      await fetchMaterials();
      setShowCreateModal(false);
    }
  };

  const handleUpdateMaterial = async (materialData) => {
    if (!editingMaterial) return;
    const { data, error } = await materialService?.updateMaterial(editingMaterial?.id, materialData);
    if (error) {
      alert(`Error updating material: ${error}`);
    } else {
      await fetchMaterials();
      setShowCreateModal(false);
      setEditingMaterial(null);
    }
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setShowCreateModal(true);
  };

  const handleDeleteMaterial = (material) => {
    setDeletingMaterial(material);
  };

  const handleConfirmDelete = async () => {
    if (!deletingMaterial) return;
    const { error } = await materialService?.deleteMaterial(deletingMaterial?.id);
    if (error) {
      alert(`Error deleting material: ${error}`);
    } else {
      await fetchMaterials();
      setDeletingMaterial(null);
    }
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedMaterials?.length === 0) return;
    const { error } = await materialService?.bulkDeleteMaterials(selectedMaterials);
    if (error) {
      alert(`Error deleting materials: ${error}`);
    } else {
      await fetchMaterials();
      setSelectedMaterials([]);
      setShowBulkDeleteModal(false);
    }
  };

  const handleConfirmClearAll = async () => {
    const { error } = await materialService?.bulkDeleteMaterials(materials?.map(m => m?.id));
    if (error) {
      alert(`Error deleting all materials: ${error}`);
    } else {
      await fetchMaterials();
      setShowClearAllModal(false);
    }
  };

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'Production', label: 'Production' },
    { value: 'Quality', label: 'Quality' },
    { value: 'Maintenance', label: 'Maintenance' },
    { value: 'Warehouse', label: 'Warehouse' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'Raw Materials', label: 'Raw Materials' },
    { value: 'Components', label: 'Components' },
    { value: 'Consumables', label: 'Consumables' },
    { value: 'Finished Goods', label: 'Finished Goods' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'discontinued', label: 'Discontinued' }
  ];

  const lowStockMaterials = materials?.filter(m => m?.inventory_level <= m?.reorder_level)?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} />
      <QuickActionsToolbar />
      <div className={`transition-all duration-250 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center gap-4">
              <button
                className="hidden lg:flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                aria-label="Toggle sidebar"
              >
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                  Material Management Database
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Comprehensive material library with cost tracking and inventory levels
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter />
              <Button
                onClick={() => {
                  window.location.href = '/material-category-management';
                }}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Icon name="Settings" size={18} />
                <span className="hidden md:inline">Material Config</span>
              </Button>
              {materials?.length > 0 && (
                <Button
                  onClick={() => setShowClearAllModal(true)}
                  variant="outline"
                  className="flex items-center gap-2 text-destructive hover:bg-destructive/10"
                >
                  <Icon name="Trash2" size={18} />
                  <span className="hidden md:inline">Clear All</span>
                </Button>
              )}
              <Button
                onClick={() => {
                  setEditingMaterial(null);
                  setShowCreateModal(true);
                }}
                className="flex items-center gap-2"
              >
                <Icon name="Plus" size={18} />
                <span className="hidden md:inline">Create Material</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="space-y-4 md:space-y-6">
            {/* Stats Bar */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Package" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Materials</p>
                    <p className="text-2xl font-semibold text-foreground">{materials?.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active Materials</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {materials?.filter(m => m?.status === 'active')?.length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
                    <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Low Stock Items</p>
                    <p className="text-2xl font-semibold text-foreground">{lowStockMaterials}</p>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="DollarSign" size={20} color="var(--color-warning)" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Value</p>
                    <p className="text-2xl font-semibold text-foreground">
                      ${materials?.reduce((sum, m) => sum + (m?.unit_cost * m?.inventory_level), 0)?.toFixed(0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  type="search"
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                />
                <Select
                  options={departmentOptions}
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e?.target?.value)}
                />
                <Select
                  options={typeOptions}
                  value={filterType}
                  onChange={(e) => setFilterType(e?.target?.value)}
                />
                <Select
                  options={statusOptions}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                {filteredMaterials?.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMaterials?.length === filteredMaterials?.length && filteredMaterials?.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-0"
                    />
                    <span className="text-sm font-medium text-foreground">Select All</span>
                  </label>
                )}
                {selectedMaterials?.length > 0 && (
                  <>
                    <span className="text-sm text-muted-foreground">
                      {selectedMaterials?.length} selected
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedMaterials([])}
                      className="flex items-center gap-2"
                    >
                      <Icon name="X" size={16} />
                      Clear
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedMaterials?.length > 0 && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="CheckSquare" size={20} color="var(--color-primary)" />
                  <span className="font-medium text-foreground">
                    {selectedMaterials?.length} material{selectedMaterials?.length > 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Icon name="Download" size={16} className="mr-2" />
                    Export
                  </Button>
                  <Button variant="outline" size="sm">
                    <Icon name="Edit" size={16} className="mr-2" />
                    Bulk Edit
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleBulkDelete}
                  >
                    <Icon name="Trash2" size={16} className="mr-2" />
                    Delete Selected
                  </Button>
                </div>
              </div>
            )}

            {/* Material Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
                <p className="text-destructive">Error loading materials: {error}</p>
              </div>
            ) : filteredMaterials?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No materials found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || filterDepartment !== 'all' || filterType !== 'all' || filterStatus !== 'all' ?'Try adjusting your filters' :'Get started by creating your first material'}
                </p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Icon name="Plus" size={18} className="mr-2" />
                  Create Material
                </Button>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <Checkbox
                            checked={selectedMaterials?.length === filteredMaterials?.length && filteredMaterials?.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Material Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">SKU</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Current Stock</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Unit Cost</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredMaterials?.map(material => {
                        const getInventoryStatus = () => {
                          if (material?.inventory_level <= material?.reorder_level) {
                            return { label: 'Low Stock', color: 'text-destructive' };
                          } else if (material?.inventory_level <= material?.reorder_level * 1.5) {
                            return { label: 'Medium Stock', color: 'text-warning' };
                          }
                          return { label: 'In Stock', color: 'text-success' };
                        };

                        const getStatusColor = (status) => {
                          switch (status) {
                            case 'active':
                              return 'bg-success/10 text-success';
                            case 'inactive':
                              return 'bg-warning/10 text-warning';
                            case 'discontinued':
                              return 'bg-destructive/10 text-destructive';
                            default:
                              return 'bg-muted text-muted-foreground';
                          }
                        };

                        const inventoryStatus = getInventoryStatus();

                        return (
                          <tr key={material?.id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <Checkbox
                                checked={selectedMaterials?.includes(material?.id)}
                                onChange={() => handleSelectMaterial(material?.id)}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-foreground">{material?.name}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-muted-foreground">{material?.sku}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-foreground">{material?.department}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-foreground">{material?.material_type}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-foreground">{material?.material_category}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className={`text-sm font-medium ${inventoryStatus?.color}`}>
                                {material?.inventory_level} {material?.unit_of_measure}
                              </div>
                              <div className="text-xs text-muted-foreground">{inventoryStatus?.label}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-semibold text-foreground">
                                ${material?.unit_cost?.toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(material?.status)}`}>
                                {material?.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditMaterial(material)}
                                  className="h-8 w-8"
                                >
                                  <Icon name="Edit" size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteMaterial(material)}
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                >
                                  <Icon name="Trash2" size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <MaterialFormModal
          onClose={() => {
            setShowCreateModal(false);
            setEditingMaterial(null);
          }}
          onSubmit={editingMaterial ? handleUpdateMaterial : handleCreateMaterial}
          editingMaterial={editingMaterial}
        />
      )}

      {deletingMaterial && (
        <DeleteConfirmationModal
          isOpen={!!deletingMaterial}
          onClose={() => setDeletingMaterial(null)}
          onConfirm={handleConfirmDelete}
          itemName={deletingMaterial?.name}
          itemType="material"
          warningMessage={`This will permanently delete the material "${deletingMaterial?.name}" and all associated data. This action cannot be undone.`}
          additionalInfo=""
        />
      )}

      {showBulkDeleteModal && (
        <DeleteConfirmationModal
          isOpen={showBulkDeleteModal}
          onClose={() => setShowBulkDeleteModal(false)}
          onConfirm={handleConfirmBulkDelete}
          title="Delete Selected Materials"
          message={`Are you sure you want to delete ${selectedMaterials?.length} selected material(s)? This action cannot be undone.`}
          itemName={`${selectedMaterials?.length} materials`}
          itemType="bulk materials"
          warningMessage={`This will permanently delete ${selectedMaterials?.length} selected material(s) and all associated data. This action cannot be undone.`}
          additionalInfo=""
        />
      )}

      {showClearAllModal && (
        <DeleteConfirmationModal
          isOpen={showClearAllModal}
          onClose={() => setShowClearAllModal(false)}
          onConfirm={handleConfirmClearAll}
          title="Clear All Materials"
          message={`Are you sure you want to delete ALL ${materials?.length} materials from the database? This will permanently remove all material records and cannot be undone.`}
          itemName="All materials"
          itemType="all materials"
          warningMessage={`This will permanently delete ALL ${materials?.length} materials from the database and all associated data. This action cannot be undone.`}
          additionalInfo=""
        />
      )}
    </div>
  );
};

export default MaterialManagementDatabase;